import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ExternalLink, ImagePlus, Loader2, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/layout/page-header';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuthStore, useBusiness } from '@/features/auth/auth.store';
import { features } from '@/config/features';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';
import { LocationMap } from './location-map';
import {
  FEE_TYPES,
  toBusinessInput,
  useBusinessInfo,
  useUpdateBusinessInfo,
  useUploadImage,
  type BusinessInput,
  type LocationInput,
} from './business-info.api';

/**
 * The store's own details.
 *
 * Business and first location are edited as one form, because nobody thinks of
 * a shop's address as belonging to something other than the shop. Three tabs,
 * following the original: what it is, how pickup works, and where it is.
 */

/** The hours a pickup window can start or end on. */
const HOURS = Array.from({ length: 25 }, (_, hour) => ({
  value: hour,
  label:
    hour === 0 || hour === 24
      ? '12:00 AM'
      : hour === 12
        ? '12:00 PM'
        : hour < 12
          ? `${hour}:00 AM`
          : `${hour - 12}:00 PM`,
}));

export function BusinessInfoPage() {
  const { t } = useTranslation();
  const business = useBusiness();
  const info = useBusinessInfo(business?.id ?? null);

  const update = useUpdateBusinessInfo();
  const upload = useUploadImage('backgroundimages');

  // Platform-wide and not editable here; the original showed them greyed out
  // beside the store's own fee so the two could be compared.
  const globalFee = useAuthStore((s) => s.state?.orderServiceFee ?? null);
  const globalFeeIsFixed = useAuthStore(
    (s) => s.state?.isOrderServiceFeeFixed ?? null
  );

  const [draft, setDraft] = useState<BusinessInput | null>(null);
  const [hydrated, setHydrated] = useState<string | null>(null);

  if (info.data && hydrated !== info.data.id) {
    setHydrated(info.data.id);
    setDraft(toBusinessInput(info.data));
  }

  if (info.isPending || !draft) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={t('businessInfo.title')}
          description={business?.name ?? ''}
        />
        {/* The query waits for a store, so without one it would skeleton forever. */}
        {!business || info.isError ? (
          <p className="text-muted-foreground rounded-xl border border-dashed py-12 text-center text-sm">
            {t(business ? 'businessInfo.loadFailed' : 'common.noStoreSelected')}
          </p>
        ) : (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} className="h-12 rounded-xl" />
            ))}
          </div>
        )}
      </div>
    );
  }

  const location = draft.locations[0] as LocationInput | undefined;

  const set = (patch: Partial<BusinessInput>) =>
    setDraft((current) => (current ? { ...current, ...patch } : current));

  /** Only the first location is edited; the rest ride along untouched. */
  const setLocation = (patch: Partial<LocationInput>) =>
    setDraft((current) => {
      if (!current || current.locations.length === 0) return current;
      const [first, ...rest] = current.locations;
      return { ...current, locations: [{ ...first, ...patch }, ...rest] };
    });

  const failed = () => toast.error(t('businessInfo.actionFailed'));

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('businessInfo.title')}
        description={business?.name ?? t('businessInfo.subtitle')}
        actions={
          <Button
            size="sm"
            disabled={update.isPending}
            onClick={() =>
              update.mutate(draft, {
                onSuccess: () => toast.success(t('businessInfo.saved')),
                onError: failed,
              })
            }
          >
            {update.isPending && <Loader2 className="animate-spin" />}
            {t('common.save')}
          </Button>
        }
      />

      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general">{t('businessInfo.general')}</TabsTrigger>
          <TabsTrigger value="pickup">{t('businessInfo.pickup')}</TabsTrigger>
          <TabsTrigger value="place">{t('businessInfo.place')}</TabsTrigger>
        </TabsList>

        {/* ------------------------------------------------------ general */}
        <TabsContent value="general" className="space-y-4">
          <section className="space-y-3 rounded-xl border p-4">
            <h2 className="text-sm font-medium">
              {t('businessInfo.theBusiness')}
            </h2>

            <Field label={t('businessInfo.businessName')}>
              <Input
                value={draft.name ?? ''}
                onChange={(event) => set({ name: event.target.value })}
              />
            </Field>

            <div className="space-y-1.5">
              <Label>{t('businessInfo.backgroundImage')}</Label>
              {draft.backgroundImage && (
                <img
                  src={draft.backgroundImage}
                  alt=""
                  className="h-28 w-full rounded-lg border object-cover"
                />
              )}
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  value={draft.backgroundImage ?? ''}
                  placeholder={t('businessInfo.imageUrl')}
                  onChange={(event) =>
                    set({ backgroundImage: event.target.value })
                  }
                />
                <Button
                  variant="outline"
                  disabled={upload.isPending}
                  onClick={() =>
                    document.getElementById('background-upload')?.click()
                  }
                >
                  {upload.isPending ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <ImagePlus className="size-4" />
                  )}
                  {t('businessInfo.upload')}
                </Button>
                <input
                  id="background-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (!file) return;
                    upload.mutate(file, {
                      onSuccess: (url) => set({ backgroundImage: url }),
                      onError: () =>
                        toast.error(t('businessInfo.uploadFailed')),
                    });
                  }}
                />
              </div>
            </div>

            <div className="grid gap-2 sm:grid-cols-3">
              <Toggle
                label={t('businessInfo.isAlpha')}
                checked={draft.isAlpha}
                onChange={(isAlpha) => set({ isAlpha })}
              />
              <Toggle
                label={t('businessInfo.isBeta')}
                checked={draft.isBeta}
                onChange={(isBeta) => set({ isBeta })}
              />
              <Toggle
                label={t('businessInfo.visibleInBeta')}
                checked={draft.visibleInBeta}
                onChange={(visibleInBeta) => set({ visibleInBeta })}
              />
            </div>
          </section>

          {location && (
            <section className="space-y-3 rounded-xl border p-4">
              <h2 className="text-sm font-medium">
                {t('businessInfo.theLocation')}
              </h2>

              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={t('businessInfo.locationName')}>
                  <Input
                    value={location.name ?? ''}
                    onChange={(event) =>
                      setLocation({ name: event.target.value })
                    }
                  />
                </Field>
                <Field label={t('businessInfo.storePhone')}>
                  <Input
                    value={location.orderPhone ?? ''}
                    onChange={(event) =>
                      setLocation({ orderPhone: event.target.value })
                    }
                  />
                </Field>
                <Field label={t('businessInfo.address1')}>
                  <Input
                    value={location.address1 ?? ''}
                    onChange={(event) =>
                      setLocation({ address1: event.target.value })
                    }
                  />
                </Field>
                <Field label={t('businessInfo.address2')}>
                  <Input
                    value={location.address2 ?? ''}
                    onChange={(event) =>
                      setLocation({ address2: event.target.value })
                    }
                  />
                </Field>
                <Field label={t('businessInfo.email')}>
                  <Input
                    type="email"
                    value={location.orderEmail ?? ''}
                    onChange={(event) =>
                      setLocation({ orderEmail: event.target.value })
                    }
                  />
                </Field>
                <Field
                  label={t('businessInfo.smsAudienceId')}
                  hint={t('businessInfo.smsAudienceIdHint')}
                >
                  <Input
                    value={location.smsAudienceId ?? ''}
                    onChange={(event) =>
                      setLocation({ smsAudienceId: event.target.value })
                    }
                  />
                </Field>
              </div>

              <div className="grid gap-2 sm:grid-cols-3">
                <Toggle
                  label={t('businessInfo.allowDelivery')}
                  checked={location.isDeliveryAvailable}
                  onChange={(isDeliveryAvailable) =>
                    setLocation({ isDeliveryAvailable })
                  }
                />
                <Toggle
                  label={t('businessInfo.allowPickup')}
                  checked={location.isPickUpAvailable}
                  onChange={(isPickUpAvailable) =>
                    setLocation({ isPickUpAvailable })
                  }
                />
                <Toggle
                  label={t('businessInfo.posIntegration')}
                  checked={location.posintegrationActive}
                  onChange={(posintegrationActive) =>
                    setLocation({ posintegrationActive })
                  }
                />
              </div>
            </section>
          )}
        </TabsContent>

        {/* ------------------------------------------------------- pickup */}
        <TabsContent value="pickup" className="space-y-4">
          {location && (
            <>
              <section className="space-y-3 rounded-xl border p-4">
                <h2 className="text-sm font-medium">
                  {t('businessInfo.theWindow')}
                </h2>

                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label={t('businessInfo.pickupStartHour')}>
                    <HourSelect
                      value={location.pickupStartHour}
                      onChange={(pickupStartHour) =>
                        setLocation({ pickupStartHour })
                      }
                    />
                  </Field>
                  <Field label={t('businessInfo.pickupEndHour')}>
                    <HourSelect
                      value={location.pickupEndHour}
                      onChange={(pickupEndHour) =>
                        setLocation({ pickupEndHour })
                      }
                    />
                  </Field>
                  <Field
                    label={t('businessInfo.pickupBuffer')}
                    hint={t('businessInfo.pickupBufferHint')}
                  >
                    <NumberField
                      value={location.pickupBuffer}
                      onChange={(pickupBuffer) => setLocation({ pickupBuffer })}
                    />
                  </Field>
                  <Field
                    label={t('businessInfo.slotRange')}
                    hint={t('businessInfo.slotRangeHint')}
                  >
                    <NumberField
                      value={location.slotRange}
                      onChange={(slotRange) => setLocation({ slotRange })}
                    />
                  </Field>
                  <Field
                    label={t('businessInfo.pickupLimit')}
                    hint={t('businessInfo.pickupLimitHint')}
                  >
                    <NumberField
                      value={location.pickupLimit}
                      onChange={(pickupLimit) => setLocation({ pickupLimit })}
                    />
                  </Field>
                  <Field
                    label={t('businessInfo.futureDays')}
                    hint={t('businessInfo.futureDaysHint')}
                  >
                    <NumberField
                      value={location.futureDaysForPickup}
                      onChange={(futureDaysForPickup) =>
                        setLocation({ futureDaysForPickup })
                      }
                    />
                  </Field>
                </div>

                <Toggle
                  label={t('businessInfo.preventSameDay')}
                  hint={t('businessInfo.preventSameDayHint')}
                  checked={location.preventTodayPickupSlots}
                  onChange={(preventTodayPickupSlots) =>
                    setLocation({ preventTodayPickupSlots })
                  }
                />
              </section>

              <section className="space-y-3 rounded-xl border p-4">
                <h2 className="text-sm font-medium">
                  {t('businessInfo.theFee')}
                </h2>

                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label={t('businessInfo.storeFee')}>
                    <NumberField
                      step="0.01"
                      value={location.pickupFee}
                      onChange={(pickupFee) => setLocation({ pickupFee })}
                    />
                  </Field>
                  <Field label={t('businessInfo.storeFeeType')}>
                    <Select
                      value={String(location.pickupFeeType)}
                      onValueChange={(value) =>
                        setLocation({ pickupFeeType: Number(value) })
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={String(FEE_TYPES.none)}>
                          {t('businessInfo.feeNone')}
                        </SelectItem>
                        <SelectItem value={String(FEE_TYPES.fixed)}>
                          {t('businessInfo.feeFixed')}
                        </SelectItem>
                        <SelectItem value={String(FEE_TYPES.percentage)}>
                          {t('businessInfo.feePercentage')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                {/* Set for the whole platform, shown so the store's own fee
                    can be read against it. */}
                {globalFee !== null && (
                  <div className="bg-muted/40 flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
                    <span className="min-w-0">
                      <span className="block text-sm">
                        {t('businessInfo.globalFee')}
                      </span>
                      <span className="text-muted-foreground block text-xs">
                        {t('businessInfo.globalFeeHint')}
                      </span>
                    </span>
                    <span className="shrink-0 font-semibold tabular-nums">
                      {globalFeeIsFixed
                        ? formatCurrency(globalFee)
                        : `${globalFee}%`}
                    </span>
                  </div>
                )}
              </section>
            </>
          )}
        </TabsContent>

        {/* -------------------------------------------------------- place */}
        <TabsContent value="place" className="space-y-4">
          {location && (
            <section className="space-y-3 rounded-xl border p-4">
              <h2 className="text-sm font-medium">
                {t('businessInfo.theMap')}
              </h2>

              {/* The read DTO carries a city, the update model does not, so it
                  cannot be changed from here. The original offered a field for
                  it anyway, which quietly did nothing. */}
              <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
                <span className="min-w-0">
                  <span className="block text-sm">{t('businessInfo.city')}</span>
                  <span className="text-muted-foreground block text-xs">
                    {t('businessInfo.cityHint')}
                  </span>
                </span>
                <span className="shrink-0 text-sm font-medium">
                  {info.data?.locations?.[0]?.city || '—'}
                </span>
              </div>

              {/* Directions is prose and wants the room; the coordinates are
                  a pair and read as one. */}
              <Field label={t('businessInfo.directions')}>
                <Input
                  value={location.directionsText ?? ''}
                  onChange={(event) =>
                    setLocation({ directionsText: event.target.value })
                  }
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label={t('businessInfo.lat')}>
                  <Input
                    value={location.lat ?? ''}
                    inputMode="decimal"
                    onChange={(event) =>
                      setLocation({ lat: event.target.value })
                    }
                  />
                </Field>
                <Field label={t('businessInfo.lon')}>
                  <Input
                    value={location.lon ?? ''}
                    inputMode="decimal"
                    onChange={(event) =>
                      setLocation({ lon: event.target.value })
                    }
                  />
                </Field>
              </div>

              {features.maps ? (
                <LocationMap
                  lat={location.lat}
                  lon={location.lon}
                  onChange={(position) => setLocation(position)}
                />
              ) : (
                <p className="text-muted-foreground rounded-lg border border-dashed py-10 text-center text-sm">
                  {t('businessInfo.noMapKey')}
                </p>
              )}

              {location.lat && location.lon && (
                <a
                  className="text-primary inline-flex items-center gap-1 text-sm hover:underline"
                  href={`https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lon}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <MapPin className="size-3.5" />
                  {t('businessInfo.openInMaps')}
                  <ExternalLink className="size-3" />
                </a>
              )}
            </section>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
  );
}

function NumberField({
  value,
  onChange,
  step,
}: {
  value: number;
  onChange: (value: number) => void;
  step?: string;
}) {
  return (
    <Input
      type="number"
      inputMode="decimal"
      min={0}
      step={step}
      value={value}
      onChange={(event) => onChange(Number(event.target.value))}
    />
  );
}

function HourSelect({
  value,
  onChange,
}: {
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <Select
      value={String(value)}
      onValueChange={(next) => onChange(Number(next))}
    >
      <SelectTrigger className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {HOURS.map((hour) => (
          <SelectItem key={hour.value} value={String(hour.value)}>
            {hour.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label
      className={cn(
        'flex items-center justify-between gap-3 rounded-lg border px-3 py-2'
      )}
    >
      <span className="min-w-0">
        <span className="block text-sm">{label}</span>
        {hint && (
          <span className="text-muted-foreground block text-xs">{hint}</span>
        )}
      </span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}
